import { useEffect, useState } from "react";
import { Badge, Box, Button, IconButton, Skeleton } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { DaymapDialog, NoItems, NotificationList, NotificationType, useNotifications, type Notification } from "daymap-react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import CheckListIcon from '@mui/icons-material/CheckList';
import ExpandMore from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";

export const NotificationButton:React.FC = () => {
    const { notifications, setAllRead } = useNotifications();
    const [modalOpen, setModalOpen] = useState(false);

    const dialogTitle = <Box display="flex" justifyContent="space-between" gap={2}>
        <span>Timeline</span>
        <Button size="small" startIcon={<CheckListIcon/>} onClick={setAllRead}>Mark all read</Button>
    </Box>
    
    return <Badge overlap="circular" badgeContent={notifications?.filter(n => !n.Read).length} anchorOrigin={{ vertical: 'bottom' }}>
        <IconButton disableFocusRipple aria-label="notifications" variant="background" onClick={() => setModalOpen(true)}>
            <NotificationsIcon />
        </IconButton>
        <DaymapDialog title={dialogTitle} open={modalOpen} onClose={() => setModalOpen(false)}>
            <Notifications inModal />
        </DaymapDialog>
    </Badge>;
}

export function Notifications({inModal}:{inModal?:boolean}) {
    const { notifications, isPending, initialFetchComplete, fetchNextPage, isFetchingNextPage, hasNextPage, setRead } = useNotifications();
    const [firstLoad, setFirstLoad] = useState<boolean>();
    const navigate = useNavigate();

    useEffect(() => {
        if (typeof firstLoad === 'undefined') {
            setFirstLoad(true);
        } else if (firstLoad === true) {
        }
    }, [notifications])

    const getNext = () => {

    }

    if (isPending)
        return <>
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
        </>

    if (notifications!.length === 0)
        return <NoItems>No timeline events</NoItems>;

    const notificationsWithClick = notifications!.map(n => {
        const url = getNotificationUrl(n);
        return {...n, onClick: url ? () => {
            setRead(n.NotificationID); 
            navigate(url);
        } : undefined};
    });

    return <Box display="flex" flexDirection="column" gap={1} pb={2}>
        <NotificationList growIn={initialFetchComplete} getNext={getNext} notifications={notificationsWithClick} inModal={inModal} />
        {isFetchingNextPage && <>
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
        </>}
        {!isFetchingNextPage && hasNextPage && <Button startIcon={<ExpandMore />} onClick={() => fetchNextPage()}>Load more</Button>}
        {!isFetchingNextPage && !hasNextPage && <NoItems>End of timeline</NoItems>}
    </Box>
}

export const getNotificationUrl = ({ StudentID, Properties, ObjectID, SentAt, ...rest }: Notification) => {
    switch (rest.NotificationType) {
        case NotificationType.PointsAdded:
            return `/student/${StudentID}/points?rowId=${ObjectID}`
        case NotificationType.UnapprovedAbsence:
            let absenceDate: Date | undefined;
            if (Properties.AbsenceDate && dayjs(Properties.AbsenceDate, "DD/MM/YYYY").isValid()) {
                absenceDate = dayjs(Properties.AbsenceDate, "DD/MM/YYYY").toDate();
            }
            else if (SentAt) {
                absenceDate = SentAt;
            }
            
            return `/message/new/6?student=${StudentID}${absenceDate ? `&date=${dayjs(absenceDate).format("YYYY-MM-DD")}` : ''}`
        case NotificationType.ParentTeacherInterview:
            return `/interviews/${ObjectID}`;
        case NotificationType.PeerToPeerMessage:
            return `/message/${ObjectID}`;
        case NotificationType.ProjectCreated:
        case NotificationType.ProjectEventAdded:
        case NotificationType.ProjectEventAttended:
        case NotificationType.ProjectEventAbsent:
        case NotificationType.ProjectEventReminder:
            return Properties.MessageID ? `/message/${Properties.MessageID}` : '';
        case NotificationType.Excursion:
        case NotificationType.CalendarEventUpdated:
        case NotificationType.CalendarEventCreated:
            return `/student/${StudentID}/excursion/${ObjectID}`;
        default: 
            return ''
    }
}

export const getNotificationsWithOnClick = (notifications:Notification[], navFunction: NavigateFunction, NotificationRead:(id:number)=>void) => {
    return notifications.map(n => {
        const url = getNotificationUrl(n);
        return {...n, onClick: url ? () => {NotificationRead(n.NotificationID); navFunction(url)} : undefined}
    })
}